/**
 * FullProfileBody — Sprint B (Feb 2026)
 *
 * Single shared profile-body renderer used by both:
 *   - Discover (Encounters detail view) — sticky action bar is caller-owned
 *   - ProfileDetail (dedicated route) — all optional slot props filled in
 *
 * Contract (per Sprint B spec):
 *   - Does NOT own the outer ScrollView. Caller wraps.
 *   - Name/age/city/distance row lives BELOW the hero, not overlaid.
 *   - Photos interleave between text sections via nextPhoto() cursor.
 *     Never two photos in a row.
 *   - Report/Block safety row is at the BOTTOM, neutral gray (NOT red).
 *   - Hero is a horizontal paged scroll with dots + N/M counter chip.
 *
 * Test-IDs match the mobile parity spec.
 */
import { useRef, useState, useEffect, useMemo } from 'react';
import VoiceBubble from '@/components/VoiceBubble';
import {
  Heart, MapPin, ArrowLeft, Flag, ShieldBan, Calendar,
  Briefcase, Building2, GraduationCap, Sparkles, Ruler, Users, UtensilsCrossed,
  PawPrint, Vote, Brain, Activity, Wine, Cigarette, Cannabis, Pill, Moon, Baby, Church, Globe,
} from 'lucide-react';
import ProfileDetailRow from './ProfileDetailRow';
import { formatHeight } from '@/utils/units';

/* ------------------------------------------------------------------ */
/* Small heart badge used by the "compat" summary chip (30px, no %).   */
/* ------------------------------------------------------------------ */
function CompatHeart({ size = 30, score = 0 }) {
  const s = Math.max(0, Math.min(100, score || 0));
  const fillPct = s / 100;
  const HEART_TOP = 5;
  const HEART_H = 16;
  const fillH = fillPct * HEART_H;
  const fillY = HEART_TOP + (HEART_H - fillH);
  const clipId = `fpb-heart-${size}`;
  return (
    <svg viewBox="0 0 24 22" width={size} height={size} className="drop-shadow-[0_1px_2px_rgba(0,0,0,0.2)]">
      <defs>
        <clipPath id={clipId}>
          <path d="M12 21s-8-4.5-8-11a5 5 0 0 1 9-3 5 5 0 0 1 9 3c0 6.5-8 11-8 11h-2z" />
        </clipPath>
        <linearGradient id={`fpb-heartGrad-${size}`} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#991B1B" />
          <stop offset="100%" stopColor="#EF4444" />
        </linearGradient>
      </defs>
      <path
        d="M12 21s-8-4.5-8-11a5 5 0 0 1 9-3 5 5 0 0 1 9 3c0 6.5-8 11-8 11h-2z"
        fill="rgba(219,68,68,0.12)" stroke="#DC2626" strokeWidth="1.2" strokeLinejoin="round"
      />
      <rect x="0" y={fillY} width="24" height={fillH}
        fill={`url(#fpb-heartGrad-${size})`} clipPath={`url(#${clipId})`} />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Hero — horizontal paged photo scroll with dots + N/M counter.       */
/* ------------------------------------------------------------------ */
function Hero({
  photos,
  onPhotoOpen,
  onBack,
  renderHeroTopRightControls,
  // June 2026 hero overlay — Discover card handoff §4/5.
  distanceText,
  displayName,
  age,
  verified,
  goalLabel,
}) {
  const scrollerRef = useRef(null);
  const [page, setPage] = useState(0);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const p = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
    if (p !== page) setPage(p);
  };

  return (
    // June 2026: edge-to-edge poster hero, aspectRatio 1:1.62 (was 1:1.1),
    // capped by 100vh − 100px on tall desktops. -mx-1/-mx-2 breaks out of
    // the ProfileBody horizontal padding so photo meets card edges.
    <div className="relative w-full rounded-[28px] overflow-hidden bg-[#E5E7EB] -mx-1 sm:-mx-2"
         style={{ aspectRatio: '1 / 1.62', maxHeight: 'calc(100vh - 100px)' }}
         data-testid="fpb-hero">
      {photos.length > 0 ? (
        <div
          ref={scrollerRef}
          onScroll={onScroll}
          className="w-full h-full overflow-x-auto overflow-y-hidden flex snap-x snap-mandatory scroll-smooth"
          style={{ scrollbarWidth: 'none' }}
        >
          {photos.map((src, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onPhotoOpen && onPhotoOpen(i)}
              data-testid={`fpb-hero-photo-${i}`}
              className="min-w-full h-full snap-center block cursor-zoom-in"
            >
              <img src={src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[#6B7280] text-sm">No photos yet</div>
      )}

      {/* Dots */}
      {photos.length > 1 && (
        <div className="absolute bottom-3.5 left-0 right-0 flex justify-center gap-[5px] pointer-events-none">
          {photos.map((_, i) => (
            <span
              key={i}
              className="rounded-full transition-all"
              style={{
                width: 6, height: 6,
                background: i === page ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.4)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.4)',
              }}
            />
          ))}
        </div>
      )}

      {/* N/M counter chip — top-right, hidden when hero-controls slot used */}
      {photos.length > 1 && !renderHeroTopRightControls && (
        <div
          data-testid="fpb-hero-counter"
          className="absolute top-3.5 right-3.5 px-2 py-[3px] rounded-full text-[11px] font-extrabold text-white"
          style={{ background: 'rgba(0,0,0,0.5)' }}
        >
          {page + 1}/{photos.length}
        </div>
      )}

      {/* Back button — only when onBack provided (detail view) */}
      {onBack && (
        <button
          onClick={onBack}
          data-testid="fpb-hero-back-btn"
          aria-label="Back"
          className="absolute top-4 left-4 w-[38px] h-[38px] rounded-full flex items-center justify-center text-white active:scale-95 transition-transform"
          style={{ background: 'rgba(0,0,0,0.5)' }}
        >
          <ArrowLeft size={20} strokeWidth={2.2} />
        </button>
      )}

      {/* Top-right controls slot — detail view uses this for Report/Block icons */}
      {renderHeroTopRightControls && (
        <div className="absolute top-4 right-4 flex gap-2 z-10">
          {renderHeroTopRightControls()}
        </div>
      )}

      {/* June 2026 — smooth scrim + name overlay (Discover handoff §3/§4/§5).
          Only rendered when we have text to overlay (i.e. detail-view hero
          uses displayName; the inline-photo block below does not). */}
      {displayName && (
        <>
          <div
            aria-hidden
            className="absolute inset-x-0 bottom-0 pointer-events-none"
            style={{
              height: 320,
              background:
                'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0.85) 100%)',
            }}
          />
          <div
            className="absolute inset-x-0 bottom-0 p-5 pb-6 flex flex-col items-start gap-2 z-[5]"
            data-testid="fpb-hero-overlay"
          >
            {/* 1. Distance pill (top of stack) */}
            {distanceText && (
              <span
                data-testid="fpb-hero-distance-pill"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-[700] text-white"
                style={{
                  background: 'rgba(0,0,0,0.55)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  letterSpacing: '0.2px',
                }}
              >
                <MapPin size={11} />
                {distanceText}
              </span>
            )}
            {/* 2. Name, Age ✓ */}
            <div className="flex items-baseline gap-2 flex-wrap" data-testid="fpb-hero-name">
              <h2 className="font-display text-[34px] leading-none font-[900] tracking-[-1.2px] text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]">
                {displayName}
                {age ? <span className="font-[700] tracking-[-0.6px] text-white/90">{`, ${age}`}</span> : null}
              </h2>
              {verified && (
                <span aria-label="Verified" className="inline-flex items-center pb-0.5">
                  <svg width="22" height="22" viewBox="0 0 20 20" fill="#FFFFFF">
                    <path d="M10 0l2.4 2.1 3.2-.4.5 3.2 2.9 1.4-1.3 3 1.3 3-2.9 1.4-.5 3.2-3.2-.4L10 20l-2.4-2.1-3.2.4-.5-3.2L1 13.7l1.3-3L1 7.7l2.9-1.4.5-3.2 3.2.4L10 0z"/>
                    <path d="M6.5 10l2.3 2.3L14 7" stroke="#0F0F10" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              )}
            </div>
            {/* 3. Looking-for chip */}
            {goalLabel && (
              <span
                data-testid="fpb-hero-goal-chip"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-[700] text-white"
                style={{
                  background: 'rgba(0,0,0,0.55)',
                  border: '1px solid rgba(255,255,255,0.18)',
                }}
              >
                <Heart size={12} strokeWidth={2.4} />
                Looking for {goalLabel}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Small prompt block card.                                            */
/* ------------------------------------------------------------------ */
function PromptBlock({ label, answer }) {
  if (!answer) return null;
  return (
    <div className="bg-white border border-[var(--hairline)] rounded-[22px] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.03)]">
      <p className="text-[11px] font-[800] tracking-[0.6px] uppercase text-[#6B7280] mb-2">{label}</p>
      <p className="text-[22px] leading-[30px] font-[700] tracking-[-0.5px] text-[#0F0F10]">{answer}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Inline photo block (interleaved between text sections).             */
/* Feb 2026 Round 3 — no longer tappable. Only the hero carousel opens */
/* the lightbox; inline photos read as editorial content, not carousel */
/* entries. A small `idx/total` counter badge replaces the tap affordance.*/
/* ------------------------------------------------------------------ */

// Small wrapper so the voice-prompt card can render a bubble without knowing
// the internal `message` shape VoiceBubble expects.
function VoiceBubblePreview({ vp }) {
  if (!vp?.url) return null;
  return (
    <VoiceBubble
      message={{
        voice_url: vp.url,
        duration: vp.duration,
        waveform: vp.waveform,
      }}
      variant="profile"
      isMe={false}
    />
  );
}


function InlinePhoto({ src, idx, total }) {
  return (
    <div
      data-testid={`fpb-inline-photo-${idx}`}
      className="relative block w-full rounded-3xl overflow-hidden bg-[#E5E7EB]"
      style={{ aspectRatio: '1 / 1.1' }}
    >
      <img src={src} alt={`Photo ${idx + 1} of ${total}`} className="w-full h-full object-cover" />
      {total > 1 && (
        <span
          className="absolute top-3 right-3 px-2 py-[3px] rounded-full text-[11px] font-extrabold text-white"
          style={{ background: 'rgba(0,0,0,0.5)' }}
        >
          {idx + 1}/{total}
        </span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Intent-chip map.                                                    */
/* Covers both wizard values (long_term, life_partner, figuring_out)   */
/* AND meeting_prefs.intent enum (serious, casual, open, just_meeting).*/
/* ------------------------------------------------------------------ */
const INTENT_LABEL = {
  // Wizard / lifestyle_answers.relationship_goals values
  long_term: 'Long-term relationship',
  life_partner: 'Ready for a relationship',
  marriage: 'Ready for marriage',
  figuring_out: 'Taking it slow',
  friends: 'New friends',
  // meeting_prefs.intent values (from onboarding intent picker)
  serious: 'Serious relationship',
  casual: 'Casual dating',
  open: 'Open to either',
  just_meeting: 'Just meeting people',
};

/* Kids decode — profile.lifestyle_answers.{has_kids, wants_kids} preferred.
   Legacy `children` decoded per HAS_WANTS_KIDS_BACKEND.md + owner-confirmed
   Feb 2026 additions. Returns { has: label|null, wants: label|null }. */
const CHILDREN_LEGACY_MAP = {
  have_and_want_more:      { has: 'Yes',  wants: 'Yes' },
  have_and_dont_want:      { has: 'Yes',  wants: 'No' },
  have_and_dont_want_more: { has: 'Yes',  wants: 'No' },
  dont_have_but_want:      { has: 'No',   wants: 'Yes' },
  dont_want:               { has: 'No',   wants: 'No' },
  want_someday:            { has: 'No',   wants: 'Someday' },
  not_sure:                { has: null,   wants: 'Not sure' },
};
const HAS_KIDS_LABEL   = { yes: 'Yes', no: 'No', prefer_not_say: null };
const WANTS_KIDS_LABEL = { yes: 'Yes', no: 'No', someday: 'Someday', not_sure: 'Not sure' };
function decodeKids(profile) {
  const la = profile?.lifestyle_answers || {};
  let has   = HAS_KIDS_LABEL[la.has_kids] ?? null;
  let wants = WANTS_KIDS_LABEL[la.wants_kids] ?? null;
  if ((has == null && wants == null) && la.children && CHILDREN_LEGACY_MAP[la.children]) {
    const d = CHILDREN_LEGACY_MAP[la.children];
    has = has ?? d.has;
    wants = wants ?? d.wants;
  }
  // Legacy: also check top-level `children` if lifestyle_answers is empty.
  if ((has == null && wants == null) && profile?.children && CHILDREN_LEGACY_MAP[profile.children]) {
    const d = CHILDREN_LEGACY_MAP[profile.children];
    has = has ?? d.has;
    wants = wants ?? d.wants;
  }
  return { has, wants };
}

/* Lifestyle grid config — order matters. Empty values are filtered out.
   `children` is deliberately excluded here — kids split into two dedicated
   tiles (HAVE KIDS / WANT KIDS) rendered separately per §4. */
const LIFESTYLE_FIELDS = [
  { key: 'drinking',        icon: Wine,             label: 'Drinking' },
  { key: 'smoking',         icon: Cigarette,        label: 'Smoking' },
  { key: 'marijuana',       icon: Cannabis,         label: 'Marijuana' },   // Feb 2026 · profile display
  { key: 'drugs',           icon: Pill,             label: 'Recreational drugs' },  // Feb 2026
  { key: 'diet',            icon: UtensilsCrossed,  label: 'Diet' },
  { key: 'exercise',        icon: Activity,         label: 'Exercise' },
  { key: 'sleep_schedule',  icon: Moon,             label: 'Sleep schedule' },  // Feb 2026
  // Feb 2026 · surface the newly-collected lifestyle fields on the profile
  // display grid so users can see (and edit later) what they picked.
  { key: 'activity_level',  icon: Activity,         label: 'Activity level' },
  { key: 'ideal_weekend',   icon: Sparkles,         label: 'Ideal weekend' },
  { key: 'social_energy',   icon: Brain,            label: 'Social style' },
  { key: 'religion',        icon: Church,           label: 'Religion' },
  { key: 'politics',        icon: Vote,             label: 'Politics' },
  { key: 'education',       icon: GraduationCap,    label: 'Education' },
  { key: 'occupation',      icon: Briefcase,        label: 'Work' },
  { key: 'pets',            icon: PawPrint,         label: 'Pets' },
  { key: 'personality_type',icon: Brain,            label: 'Personality' },
  { key: 'zodiac_sign',     icon: Sparkles,         label: 'Zodiac' },
];

const cap = (v) => typeof v === 'string' ? v.replace(/_/g, ' ') : v;

/* Feb 28 2026 · Religion label overrides. Most stored values render fine
   via `cap()` + CSS `capitalize` (`orthodox_christian` → "Orthodox
   Christian"), but a handful need bespoke casing that CSS can't produce
   from a lowercase snake_case token: the hyphen in "Latter-day Saint"
   and the diacritics in "Baháʼí". Legacy tokens (`spiritual`,
   `not_religious`) are still in the map so mid-migration profiles keep
   rendering their friendly labels even before the DB migration runs.
   The backend `/api/religions` endpoint is the authoritative label
   source everywhere else — this map is display-only. */
const RELIGION_LABEL_OVERRIDES = {
  latter_day_saint:   'Latter-day Saint',
  bahai:              'Baháʼí',
  orthodox_christian: 'Orthodox Christian',
  no_religion:        'No religion',
  not_religious:      'No religion',     // legacy → canonical label
  spiritual:          'Spirituality',    // legacy → canonical label
  prefer_not_to_say:  'Prefer not to say',
};

/* ------------------------------------------------------------------ */
/* Main component.                                                     */
/* ------------------------------------------------------------------ */
export default function FullProfileBody({
  profile,
  compatScore = null,
  meetScore = null,
  meetTier = null,       // reserved (rendered inside renderMeetScorePanel slot)
  distanceKm = null,
  useMetric = false,
  onPhotoOpen,
  onCompatOpen,
  onMeetScoreOpen,
  onReport,
  onBlock,
  // Slots
  onBack,
  renderHeroTopRightControls,
  renderBadgeRow,
  renderVoiceIntro,
  renderMeetScorePanel,
  renderMeetingPrefs,
  renderCompatibilityCard,
  // Prompts list (label lookup) — passed by caller since it's a global fetch.
  promptsList = [],
  // Feb 2026 — Discover inline scroll mode. When true, the built-in Hero is
  // skipped because the caller (Discover swipe card) renders its own hero
  // above this component. All other slots and sections behave identically.
  hideHero = false,
}) {
  const photos = useMemo(
    () => (Array.isArray(profile?.photos) && profile.photos.length
      ? profile.photos
      : [profile?.photo_url]
    ).filter(Boolean),
    [profile]
  );

  // Cursor pattern — one photo between text sections. Never two photos in a row.
  // Hero is index 0, so cursor starts at 1.
  const cursorRef = useRef(1);
  useEffect(() => { cursorRef.current = 1; }, [profile?.user_id]);
  const nextPhoto = () => {
    if (cursorRef.current >= photos.length) return null;
    const uri = photos[cursorRef.current];
    const idx = cursorRef.current;
    cursorRef.current += 1;
    return { uri, idx };
  };

  const firstName = (profile?.display_name || '').split(' ')[0] || profile?.display_name || '';

  // Prompts (ordered per profile). Split into first / second / third / rest.
  // June 2026: filter empty answers + support both {question,answer} legacy
  // shape and {key,answer} new shape. See DISCOVER_CARD_WEB_HANDOFF §8.
  const prompts = Array.isArray(profile?.prompts)
    ? profile.prompts.filter((p) => p && typeof p.answer === 'string' && p.answer.trim())
    : [];
  const humanise = (v) => (v || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const promptLabel = (p) => {
    if (!p) return 'Prompt';
    const explicit = (p.question || '').trim();
    if (explicit) return explicit;
    const catalog = promptsList.find((x) => x.key === p.key)?.label;
    if (catalog) return catalog;
    if (p.key) return humanise(p.key);
    return 'Prompt';
  };

  // Relationship-goal fallback chain (Feb 2026 Round 3).
  //
  // Bug: hero "Looking for X" chip and the Intent card below were reading
  // from DIFFERENT fields — neither of them matched where seed profiles
  // actually stored the value. Result: profiles with a clear intent
  // still rendered NO chip and NO intent card.
  //
  // Backend truth (three storage locations, kept for backward compat):
  //   • Seed profiles          → answers.relationship_goals
  //   • Wizard-driven profiles → lifestyle_answers.relationship_goals
  //   • Meeting-prefs override → meeting_prefs.intent
  //   • Legacy singular        → answers.relationship_goal
  //
  // Consolidate into ONE chain, reused by BOTH the hero chip and the
  // Intent card so they can never diverge again.
  // Feb 2026 — backend now emits a canonical `relationship_goal` field on
  // /profile/{id} and /discover, computed via the same fallback chain we
  // used to run client-side. We still honor the old sub-documents so this
  // component works against older API responses (e.g. a mobile client
  // hitting a stale backend, or historical Snapshot fixtures).
  const relGoal =
    profile?.relationship_goal
    || profile?.meeting_prefs?.intent
    || profile?.lifestyle_answers?.relationship_goals
    || profile?.answers?.relationship_goals
    || profile?.answers?.relationship_goal
    || profile?.intent
    || '';

  const RELATIONSHIP_GOAL_LABEL = {
    long_term:    'Long-term relationship',
    life_partner: 'Life partner',
    marriage:     'Marriage',
    casual:       'Casual',
    figuring_out: 'Figuring it out',
    friends:      'New friends',
    // meeting_prefs.intent enum — short-form labels for the hero chip.
    serious:      'Serious relationship',
    open:         'Open to either',
    just_meeting: 'Just meeting people',
  };
  const goalChipLabel = relGoal ? (RELATIONSHIP_GOAL_LABEL[relGoal] || cap(relGoal)) : null;
  const intentLabel   = relGoal ? (INTENT_LABEL[relGoal] || RELATIONSHIP_GOAL_LABEL[relGoal] || cap(relGoal)) : null;

  const lifestyleRows = LIFESTYLE_FIELDS
    .map((f) => ({ ...f, value: profile?.[f.key] || profile?.lifestyle_answers?.[f.key] }))
    .filter((f) => !!f.value && f.value !== 'prefer_not_to_say');

  // Values (compat answer keys)
  const VALUES_KEYS = ['relationship_goals', 'love_language', 'communication_style', 'family_plans', 'social_energy', 'lifestyle', 'values', 'faith', 'work_life_balance', 'deal_breaker'];
  const valuesEntries = profile?.answers
    ? VALUES_KEYS
        .map((k) => [k, profile.answers[k]])
        .filter(([, v]) => !!v)
    : [];

  // Interests (up to 20 pills)
  const interests = Array.isArray(profile?.interests) ? profile.interests.slice(0, 20) : [];
  // Languages (blue pills, kept separate)
  const languages = Array.isArray(profile?.languages) ? profile.languages : [];

  // The distance line follows spec: `{city}{distance ? ' · ' + distance + ' km' : ''}`
  // We honor useMetric via caller-side conversion; keep it simple + label with unit.
  const distLine = () => {
    if (distanceKm == null) return null;
    if (useMetric) return `${Math.round(distanceKm)} km`;
    const mi = distanceKm * 0.621371;
    if (mi < 1) return 'Less than 1 mi';
    return `${Math.round(mi)} mi`;
  };

  return (
    <div className="w-full max-w-full text-[#0F0F10]" data-testid="full-profile-body">
      {/* 1. Hero */}
      {!hideHero && (
        <Hero
          photos={photos}
          onPhotoOpen={onPhotoOpen}
          onBack={onBack}
          renderHeroTopRightControls={renderHeroTopRightControls}
          distanceText={distLine() ? `${distLine()} away` : null}
          displayName={profile?.display_name}
          age={profile?.age}
          verified={(profile?.badges || []).includes('verified')}
          goalLabel={goalChipLabel}
        />
      )}

      <div className="px-1 sm:px-2 pt-4 pb-2 space-y-4">
        {/* 2. Essentials row — BELOW hero.
            June 2026: name/age/verified/location are now on the hero overlay.
            This row only renders when work info is present. See handoff §6. */}
        {(() => {
          const workValue = (profile?.job_title
            ? (profile?.company ? `${profile.job_title} · ${profile.company}` : profile.job_title)
            : (profile?.occupation || profile?.industry)) || null;
          if (!workValue) return null;
          return (
            <div className="flex items-center gap-1.5 text-[13px] text-[#6B7280] font-semibold" data-testid="fpb-work-row">
              <Briefcase size={12} className="opacity-70" />
              <span className="truncate">{workValue}</span>
            </div>
          );
        })()}

        {/* 3. Summary chips — Compat + Meet Score */}
        {(compatScore > 0 || meetScore > 0) && (
          <div className="flex items-center gap-2.5 flex-wrap" data-testid="fpb-summary-chips">
            {compatScore > 0 && (
              <button
                type="button"
                onClick={onCompatOpen}
                data-testid="fpb-compat-chip"
                className="inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-1 bg-white border border-[var(--hairline)] shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_2px_6px_rgba(0,0,0,0.08)] transition-shadow"
              >
                <CompatHeart score={compatScore} />
                <span className="font-display text-[15px] font-[800] tracking-tight text-[#0F0F10]">{Math.round(compatScore)}%</span>
              </button>
            )}
            {meetScore > 0 && (
              <button
                type="button"
                onClick={onMeetScoreOpen}
                data-testid="fpb-meet-score-chip"
                className="inline-flex items-center gap-2 rounded-full pl-1.5 pr-3 py-1 bg-white border border-[var(--hairline)] shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_2px_6px_rgba(0,0,0,0.08)] transition-shadow"
              >
                <span className="w-7 h-7 rounded-full bg-[#DBEAFE] flex items-center justify-center">
                  <Calendar size={14} className="text-[#1D4ED8]" strokeWidth={2.4}/>
                </span>
                <span className="font-display text-[15px] font-[800] tracking-tight text-[#0F0F10]">{Math.round(meetScore)}</span>
              </button>
            )}
          </div>
        )}

        {/* 4. Badge row slot */}
        {renderBadgeRow && renderBadgeRow()}

        {/* 5. Intent card — neutral surface, ink-black icon chip (no red gradient). */}
        {intentLabel && (
          <div
            data-testid="fpb-intent-card"
            className="flex items-center gap-3 rounded-2xl px-4 py-3 bg-white border border-[var(--hairline)]"
          >
            <span className="w-10 h-10 rounded-full bg-[#F4F5F8] flex items-center justify-center flex-shrink-0">
              <Heart size={18} strokeWidth={2.4} className="text-[#0F0F10]" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-[800] tracking-[0.6px] uppercase text-[#6B7280]">Looking for</p>
              <p className="text-[15px] font-[800] text-[#0F0F10] leading-tight mt-[1px]">{intentLabel}</p>
            </div>
          </div>
        )}

        {/* 6. Bio card — About me (22pt heading, no italic quote). */}
        {profile?.bio && (
          <div className="bg-white border border-[var(--hairline)] rounded-[22px] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.03)]" data-testid="fpb-bio">
            <p className="text-[22px] font-[800] tracking-[-0.6px] leading-snug text-[#0F0F10] mb-2">About me</p>
            <p className="text-[15px] leading-relaxed text-[#0F0F10]">{profile.bio}</p>
          </div>
        )}

        {/* 7. Voice intro slot */}
        {renderVoiceIntro && renderVoiceIntro()}

        {/* 7b. Voice prompt — recorded via Edit Profile → About You (Feb 2026). */}
        {profile?.voice_prompt?.url && (
          <div className="bg-white border border-[var(--hairline)] rounded-[22px] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.03)]" data-testid="fpb-voice-prompt">
            <p className="text-[22px] font-[800] tracking-[-0.6px] leading-snug text-[#0F0F10] mb-3">Hear my voice</p>
            <VoiceBubblePreview vp={profile.voice_prompt} />
          </div>
        )}

        {/* 8. Meet Score panel slot (detailed breakdown) */}
        {renderMeetScorePanel && renderMeetScorePanel()}

        {/* 9. Meeting prefs slot */}
        {renderMeetingPrefs && renderMeetingPrefs()}

        {/* 10. Compatibility card slot */}
        {renderCompatibilityCard && renderCompatibilityCard()}

        {/* 11. Photo 1 */}
        {(() => { const p = nextPhoto(); return p ? <InlinePhoto src={p.uri} idx={p.idx} total={photos.length} /> : null; })()}

        {/* 12. First prompt */}
        {prompts[0] && <PromptBlock label={promptLabel(prompts[0])} answer={prompts[0].answer} />}

        {/* 13. Interests — neutral grey chips (surfaceMuted bg, ink text). */}
        {interests.length > 0 && (
          <div data-testid="fpb-interests">
            <p className="text-[22px] font-[800] tracking-[-0.6px] leading-snug text-[#0F0F10] mb-2.5">Interests</p>
            <div className="flex flex-wrap gap-2">
              {interests.map((i) => (
                <span key={i} className="px-3.5 py-2 rounded-full text-[14px] font-[600] bg-[#F4F5F8] text-[#0F0F10] border border-[rgba(15,15,16,0.06)]">
                  {i}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* 14. Photo 2 */}
        {(() => { const p = nextPhoto(); return p ? <InlinePhoto src={p.uri} idx={p.idx} total={photos.length} /> : null; })()}

        {/* 15. Second prompt */}
        {prompts[1] && <PromptBlock label={promptLabel(prompts[1])} answer={prompts[1].answer} />}

        {/* 16. Lifestyle 2-col grid — with split HAVE KIDS / WANT KIDS tiles. */}
        {(() => {
          const kids = decodeKids(profile);
          const kidsTiles = [];
          if (kids.has != null)   kidsTiles.push({ key: 'have_kids', label: 'Have kids', value: kids.has });
          if (kids.wants != null) kidsTiles.push({ key: 'want_kids', label: 'Want kids', value: kids.wants });
          const showSection = lifestyleRows.length > 0 || profile?.height_cm || languages.length > 0 || kidsTiles.length > 0;
          if (!showSection) return null;
          return (
            <div data-testid="fpb-lifestyle">
              <p className="text-[22px] font-[800] tracking-[-0.6px] leading-snug text-[#0F0F10] mb-2.5">Lifestyle</p>
              <div className="grid grid-cols-2 gap-2.5">
                {kidsTiles.map(({ key, label, value }) => (
                  <div key={key} data-testid={`fpb-tile-${key}`} className="flex items-center gap-2.5 bg-white border border-[var(--hairline)] rounded-xl p-2.5">
                    <span className="w-8 h-8 rounded-full bg-[#F4F5F8] flex items-center justify-center flex-shrink-0">
                      <Baby size={16} className="text-[#0F0F10]" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[10px] tracking-[0.6px] uppercase text-[#6B7280] font-[800]">{label}</p>
                      <p className="text-[13px] font-[700] text-[#0F0F10] capitalize truncate">{value}</p>
                    </div>
                  </div>
                ))}
                {lifestyleRows.map(({ key, icon: Icon, label, value }) => {
                  const isHeight = key === 'height_cm';
                  // Feb 28 2026 — religion label overrides for hyphenated /
                  // diacritic-bearing spellings that CSS `capitalize` can't
                  // produce from a lowercase snake_case token.
                  const isReligion = key === 'religion';
                  const religionLabel = isReligion
                    ? (RELIGION_LABEL_OVERRIDES[String(value).toLowerCase()] || cap(String(value)))
                    : null;
                  const display = isHeight ? formatHeight(value)
                                : isReligion ? religionLabel
                                : cap(String(value));
                  // Skip CSS `capitalize` on religion so "Latter-day Saint"
                  // doesn't become "Latter-Day Saint".
                  const displayClass = isReligion
                    ? "text-[13px] font-[700] text-[#0F0F10] truncate"
                    : "text-[13px] font-[700] text-[#0F0F10] capitalize truncate";
                  return (
                    <div key={key} className="flex items-center gap-2.5 bg-white border border-[var(--hairline)] rounded-xl p-2.5">
                      <span className="w-8 h-8 rounded-full bg-[#F4F5F8] flex items-center justify-center flex-shrink-0">
                        <Icon size={16} className="text-[#0F0F10]" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-[10px] tracking-[0.6px] uppercase text-[#6B7280] font-[800]">{label}</p>
                        <p className={displayClass}>{display}</p>
                      </div>
                    </div>
                  );
                })}
                {profile?.height_cm && (
                  <div className="flex items-center gap-2.5 bg-white border border-[var(--hairline)] rounded-xl p-2.5">
                    <span className="w-8 h-8 rounded-full bg-[#F4F5F8] flex items-center justify-center flex-shrink-0">
                      <Ruler size={16} className="text-[#0F0F10]" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[10px] tracking-[0.6px] uppercase text-[#6B7280] font-[800]">Height</p>
                      <p className="text-[13px] font-[700] text-[#0F0F10] truncate">{formatHeight(profile.height_cm)}</p>
                    </div>
                  </div>
                )}
                {languages.length > 0 && (
                  <div className="col-span-2 flex items-center gap-2.5 bg-white border border-[var(--hairline)] rounded-xl p-2.5">
                    <span className="w-8 h-8 rounded-full bg-[#F4F5F8] flex items-center justify-center flex-shrink-0">
                      <Globe size={16} className="text-[#0F0F10]" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] tracking-[0.6px] uppercase text-[#6B7280] font-[800]">Languages</p>
                      <p className="text-[13px] font-[700] text-[#0F0F10] truncate">{languages.join(', ')}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* 17. Photo 3 */}
        {(() => { const p = nextPhoto(); return p ? <InlinePhoto src={p.uri} idx={p.idx} total={photos.length} /> : null; })()}

        {/* 18. Third prompt */}
        {prompts[2] && <PromptBlock label={promptLabel(prompts[2])} answer={prompts[2].answer} />}

        {/* 19. Values — clean Hinge-style list with hairline dividers. */}
        {valuesEntries.length > 0 && (
          <div data-testid="fpb-values">
            <p className="text-[22px] font-[800] tracking-[-0.6px] leading-snug text-[#0F0F10] mb-1">Values</p>
            <div>
              {valuesEntries.map(([k, v], idx) => (
                <div
                  key={k}
                  className={`flex items-baseline justify-between gap-4 py-3.5 ${idx < valuesEntries.length - 1 ? 'border-b border-[var(--hairline)]' : ''}`}
                >
                  <p className="text-[13px] tracking-[-0.1px] text-[#6B7280] font-[600] capitalize">{k.replace(/_/g, ' ')}</p>
                  <p className="text-[15px] font-[700] text-[#0F0F10] capitalize text-right">
                    {typeof v === 'string' ? v.replace(/_/g, ' ') : JSON.stringify(v)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 20. Photo 4 */}
        {(() => { const p = nextPhoto(); return p ? <InlinePhoto src={p.uri} idx={p.idx} total={photos.length} /> : null; })()}

        {/* 21. Remaining prompts (each followed by a photo when available). */}
        {prompts.slice(3).map((pr, i) => (
          <div key={`rest-prompt-${i}`} className="space-y-4">
            <PromptBlock label={promptLabel(pr.key)} answer={pr.answer} />
            {(() => { const p = nextPhoto(); return p ? <InlinePhoto src={p.uri} idx={p.idx} total={photos.length} /> : null; })()}
          </div>
        ))}

        {/* 22. Leftover photos — dump any remaining. */}
        {(() => {
          const rest = [];
          let safety = 0;
          while (safety < 20) {
            const p = nextPhoto();
            if (!p) break;
            rest.push(p);
            safety += 1;
          }
          return rest.map((p) => <InlinePhoto key={`leftover-${p.idx}`} src={p.uri} idx={p.idx} total={photos.length} />);
        })()}

        {/* Details row — extra structured fields not in lifestyle grid.
            Kept for parity with the older detail dialog. Renders as a
            secondary grid, only when data exists. */}
        {(profile?.job_title || profile?.company || profile?.industry || profile?.race || profile?.body_type || profile?.sexual_orientation) && (
          <div className="grid grid-cols-2 gap-x-5 gap-y-3.5 text-sm" data-testid="fpb-details-extra">
            <ProfileDetailRow icon={Briefcase} label="Work"
              value={profile.job_title ? (profile.company ? `${profile.job_title} · ${profile.company}` : profile.job_title) : profile.occupation} />
            <ProfileDetailRow icon={Building2} label="Industry" value={
              /* Feb 28 2026 — legacy industry label rewrite. Mid-migration
                 profiles stored as "Creative" / "Trades" / "Entrepreneur"
                 render with the canonical label until the DB migration lands. */
              ({ Creative: 'Creative & Media', Trades: 'Trades & Construction', Entrepreneur: 'Entrepreneurship' })[profile.industry] || profile.industry
            } />
            <ProfileDetailRow icon={Users} label="Ethnicity" value={profile.race} />
            <ProfileDetailRow icon={Users} label="Body" value={profile.body_type} />
            <ProfileDetailRow icon={Heart} label="Orientation"
              value={profile.sexual_orientation && profile.sexual_orientation !== 'prefer_not_to_say' ? String(profile.sexual_orientation).replace(/_/g, ' ') : null} />
            {/* Feb 2026 · Phase 135 — CURRENT status. `its_complicated`
                renders as "It's complicated"; other values just prettify
                the underscore. Hidden gracefully when unset. */}
            <ProfileDetailRow icon={Heart} label="Status"
              value={(() => {
                const ms = profile.lifestyle_answers?.marital_status || profile.marital_status;
                if (!ms) return null;
                if (ms === 'its_complicated') return "It's complicated";
                return String(ms).charAt(0).toUpperCase() + String(ms).slice(1);
              })()} />
          </div>
        )}

        {/* June 2026 — Location block at the bottom of the profile.
            See DISCOVER_CARD_WEB_HANDOFF §7. Hidden gracefully if no city.
            Feb 2026 — hometown added underneath current city. */}
        {(profile?.location?.city || profile?.hometown?.city) && (
          <div className="mx-1.5 mb-3 mt-1" data-testid="fpb-location-block">
            <h3 className="text-[18px] font-[800] text-[#0F0F10] mb-2 tracking-[-0.4px]">Location</h3>
            <div
              className="rounded-2xl overflow-hidden"
              style={{ background: '#FFFFFF', border: '1px solid var(--hairline)' }}
            >
              {profile?.location?.city && (
                <div className="flex items-center gap-2 px-3.5 py-3" data-testid="fpb-current-city">
                  <MapPin size={16} className="text-[#0F0F10]" />
                  <div className="flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-[1.2px] text-[#8A8A93] leading-none mb-1">Lives in</p>
                    <span className="text-[15px] font-[600] text-[#0F0F10] tracking-[-0.2px]">
                      {profile.location.city}
                      {distLine() && <span className="text-[#6B7280]"> · {distLine()} away</span>}
                    </span>
                  </div>
                </div>
              )}
              {profile?.hometown?.city && (
                <div
                  className="flex items-center gap-2 px-3.5 py-3"
                  style={profile?.location?.city ? { borderTop: '1px solid var(--hairline)' } : {}}
                  data-testid="fpb-hometown"
                >
                  <MapPin size={16} className="text-[#6B7280]" />
                  <div className="flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-[1.2px] text-[#8A8A93] leading-none mb-1">From</p>
                    <span className="text-[15px] font-[600] text-[#0F0F10] tracking-[-0.2px]">
                      {[profile.hometown.city, profile.hometown.state, profile.hometown.country]
                        .filter(Boolean).join(', ')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 23. Safety row — neutral gray ghost buttons. NOT red. */}
        {(onReport || onBlock) && (
          <div className="pt-5 mt-2 border-t border-[var(--hairline)]" data-testid="fpb-safety-row">
            <div className="flex items-center justify-center gap-6">
              {onReport && (
                <button
                  type="button"
                  onClick={onReport}
                  data-testid="fpb-report-btn"
                  className="inline-flex items-center gap-2 py-2.5 px-3 text-[13px] font-semibold text-[#6B7280] hover:text-[#0F0F10] transition-colors"
                >
                  <Flag size={16} strokeWidth={2} />
                  <span>Report {firstName || 'user'}</span>
                </button>
              )}
              {onBlock && (
                <button
                  type="button"
                  onClick={onBlock}
                  data-testid="fpb-block-btn"
                  className="inline-flex items-center gap-2 py-2.5 px-3 text-[13px] font-semibold text-[#6B7280] hover:text-[#0F0F10] transition-colors"
                >
                  <ShieldBan size={16} strokeWidth={2} />
                  <span>Block {firstName || 'user'}</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
